import React from 'react';
import { FormatChips } from './FormatChips';
import { ShopLink } from './ShopLink';
import { CardIndicators } from './CardIndicators';
import { hexToRgb } from '../utils/colors';
import { formatCoffeeType } from '../utils/coffeeTypesCache';
import type { CoffeeRateInterface } from '../types';

interface CardHeaderProps {
    coffeeData: CoffeeRateInterface;
    coffeeIconUrl: string;
    isAdmin?: boolean;
}

/**
 * Card head — brutalist re-layout to match the site language:
 *   • boxed score badge (left), tinted by the rating colour
 *   • coffee type + a real "open the shop" action pill (centre)
 *   • the coffee-type icon, cleanly contained at the top-right (no overflow)
 *   • a row of info indicators, then the format chips
 * The rating colour drives an `--accent` custom property consumed by the CSS.
 */
export const CardHeader: React.FC<CardHeaderProps> = ({ coffeeData, coffeeIconUrl, isAdmin }) => {
    const accent = coffeeData.rateColor || '#6b7280';
    const accentRgb = hexToRgb(accent).replace(/,\s*/g, ' '); // "r g b" for rgb(... / a)
    const headStyle = {
        '--accent': accent,
        '--accent-rgb': accentRgb,
    } as React.CSSProperties;

    return (
        <div className='card-head' style={headStyle}>
            <div className='card-head__top'>
                <div
                    className='score-badge'
                    aria-label={`Rated ${coffeeData.overallRating} out of 10`}
                >
                    <span className='score-badge__num'>{coffeeData.overallRating.toPrecision(2)}</span>
                    <span className='score-badge__out'>/ 10</span>
                </div>

                <div className='card-head__main'>
                    <div className='card-head__type'>
                        {coffeeData.supermarket
                            ? (coffeeData.supermarket.type?.name ?? coffeeData.supermarket.flavor)
                            : formatCoffeeType(coffeeData.coffeeType)}
                    </div>
                    <ShopLink coffeeData={coffeeData} isAdmin={isAdmin} />
                </div>

                {coffeeIconUrl && (
                    <img
                        src={coffeeIconUrl}
                        alt=''
                        className='card-head__icon'
                        aria-hidden
                        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                    />
                )}
            </div>

            <CardIndicators coffeeData={coffeeData} />
            <FormatChips
                format={coffeeData.format}
                supermarketFormat={coffeeData.kind === 'supermarket' ? coffeeData.supermarket?.type?.format : undefined}
            />
        </div>
    );
};
